import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LocalStorageType from "../../../constants/LocalStorageType";
import { Link } from "react-router-dom";
import Common from "../../../constants/Common";
import { errorAlert, successAlert, warnAlert } from "../alert/defaultAlert";
import axiosInstance from "../../../services/AxiosInstance";
import Api from "../../../constants/Api";
import { reload } from "../../../utils/PageUtils";
import ImagePreview from "../Common/ImagePreview";
import { downloadUploadFile } from "../../../services/AxiosService";
import { convertArrayBufferToBlob } from "../../../utils/ConverterUtils";

const AccountSetting = () => {

    const { t } = useTranslation();

    const [id, setId] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [email, setEmail] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const photoChangedRef = useRef(false);

    const handlePhotoChange = (file) => {
        setUploadedFile(file);
        photoChangedRef.current = true;
    };

    const savePhotoIfChanged = (userId, onSuccess) => {
        if (!photoChangedRef.current) {
            onSuccess();
            return;
        }

        const formData = new FormData();
        if (uploadedFile) {
            const file = uploadedFile instanceof File
                ? uploadedFile
                : new File([uploadedFile], "photo.png", { type: uploadedFile.type || "image/png" });
            formData.append("file", file);
            formData.append("destDir", "user");
        }

        const config = { headers: { 'content-type': 'multipart/form-data' } };
        axiosInstance.patch(Api.identity.user + "/account-photo/" + userId, formData, config)
            .then(() => {
                photoChangedRef.current = false;
                onSuccess();
            })
            .catch(error => errorAlert(error));
    };

    const save = () => {
        if (firstName.trim() === '') {
            warnAlert(t("validation.required.fisrtName"));
            return;
        }
        if (lastName.trim() === '') {
            warnAlert(t("validation.required.lastName"));
            return;
        }
        if (email.trim() === '') {
            warnAlert(t("validation.required.email"));
            return;
        }

        // Só valida a senha caso a mesma tenha sido preenchida.
        if (password.trim() !== '') {
            if (password.length < Common.MINIMUM_PASSWORD_LENGTH) {
                warnAlert(t("message.minimum_password_length", { length: Common.MINIMUM_PASSWORD_LENGTH }));
                return;
            }
            if (passwordConfirmation.trim() === '') {
                warnAlert(t("validation.required.password_confirmation"));
                return;
            }
            if (password !== passwordConfirmation) {
                warnAlert(t("validation.invalid.password_confirmation"));
                return;
            }
        }

        // Monta a requisição
        let data = {
            firstName: firstName,
            lastName: lastName,
            email: email,
            password: password
        }

        // Envia os dados para serem atualizados.
        axiosInstance.patch(Api.identity.user + "/account/" + id, data)
            .then(() => {
                // Atualiza os dados localmente.
                let userObject = JSON.parse(localStorage.getItem(LocalStorageType.USER_DETAILS));
                userObject.email = email;
                userObject.firstName = firstName;
                userObject.lastName = lastName;
                localStorage.setItem(LocalStorageType.USER_DETAILS, JSON.stringify(userObject));

                savePhotoIfChanged(id, () => {
                    successAlert(t('message.data_updated_successfully'), () => reload());
                });
            })
            .catch(error => errorAlert(error));
    }

    useEffect(() => {
        const userObject = JSON.parse(localStorage.getItem(LocalStorageType.USER_DETAILS));
        const userId = userObject.id;
        setId(userId);
        setFirstName(userObject.firstName);
        setLastName(userObject.lastName);
        setEmail(userObject.email);

        setShowLoading(true);
        downloadUploadFile(Api.identity.user + "/download", userId)
            .then(resp => {
                if (resp.data && resp.data.byteLength > 0) {
                    setUploadedFile(convertArrayBufferToBlob(resp.data));
                }
                setShowLoading(false);
            })
            .catch(() => setShowLoading(false));
    }, []);

    return (
        <div className="card">
            <div className="card-body">
                <h3 className="mb-2">{t('account')}</h3>
                <p className="text-muted small mb-4">Gerencie suas informações pessoais e garanta a segurança da sua conta com facilidade, mantendo seus dados sempre atualizados e protegidos.</p>
                <hr className="my-4" />
                <div className="row mb-3 justify-content-center">
                    <div className="col-auto text-center">
                        <div className="avatar-upload-wrap">
                            <ImagePreview
                                id="accountPhoto"
                                permittedWidth={Common.PROFILE_IMAGE_PERMITTED_WIDTH}
                                permittedHeight={Common.PROFILE_IMAGE_PERMITTED_HEIGHT}
                                permittedSize={Common.PROFILE_IMAGE_PERMITTED_FILE_SIZE}
                                setUploadedFile={handlePhotoChange}
                                uploadedFile={uploadedFile}
                                showLoading={showLoading}
                            />
                            <span className="avatar-upload-label">{t('picture')}</span>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xl-6 col-sm-6">
                        <div className="setting-input">
                            <label htmlFor="accountFirstName" className="form-label">{t('firstName')}</label>
                            <input type="text" className="form-control" id="accountFirstName"
                                onChange={(e) => setFirstName(e.target.value)}
                                value={firstName} placeholder={t('firstName_placeholder')} maxLength={20} />
                        </div>
                    </div>
                    <div className="col-xl-6 col-sm-6">
                        <div className="setting-input">
                            <label htmlFor="lastName" className="form-label">{t('lastName')}</label>
                            <input type="text" className="form-control" id="lastName" value={lastName}
                                placeholder={t('lastName_placeholder')} maxLength={20}
                                onChange={(e) => setLastName(e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xl-12 col-sm-12">
                        <div className="setting-input">
                            <label htmlFor="accountEmail" className="form-label">{t('email')}</label>
                            <input type="email" className="form-control" id="accountEmail"
                                value={email} placeholder={t('email_placeholder')}
                                onChange={(e) => setEmail(e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xl-6 col-sm-6">
                        <div className="setting-input">
                            <label htmlFor="accountPassword" className="form-label">{t('password')}</label>
                            <input type="password" className="form-control"
                                id="accountPassword" value={password}
                                onChange={(e) => setPassword(e.target.value)} maxLength={30} />
                        </div>
                    </div>
                    <div className="col-xl-6 col-sm-6">
                        <div className="setting-input">
                            <label htmlFor="accountPasswordConfirmation" className="form-label">{t('password_confirmation')}</label>
                            <input type="password" className="form-control"
                                id="accountPasswordConfirmation" value={passwordConfirmation}
                                onChange={(e) => setPasswordConfirmation(e.target.value)} maxLength={30} />
                        </div>
                        <Link to={"#"} className="btn btn-primary float-end btn-md" onClick={() => save()}>{t('save_setting')}</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AccountSetting;